import { useTranslation } from 'react-i18next'
import { cn } from '../../../lib/utils'
import { formatText } from '../../../utils/quiz-parser'
import { KatexText } from '../shared'
import type { InlineQuizData, MultiChoiceQuizData } from '../../../types/legacyQuiz'
import { PRINT_CORRECT_ANSWER_CLASS, PrintQuizTitle, PrintQuizImage, getStyles, getImageUrl, getPrintScale } from './print-common'

const CIRCLED_NUMBERS = ['①', '②', '③', '④', '⑤', '⑥', '⑦', '⑧', '⑨', '⑩']

interface InlinePrintProps {
  quizId: string | number
  quiz: InlineQuizData
  number: number
  showCorrectAnswers?: boolean
  compact?: boolean
}

export function InlinePrint({ quizId, quiz, number, showCorrectAnswers = false, compact }: InlinePrintProps) {
  const { t } = useTranslation('quiz')
  const s = getStyles(compact)
  const imageUrl = getImageUrl(quiz.image, quiz.imagePrint)
  const printScale = getPrintScale(quiz.image, quiz.imagePrint)

  return (
    <div className={cn(s.container, s.quizSection)}>
      <PrintQuizTitle number={number} compact={compact}>
        <KatexText text={quiz.title} />
      </PrintQuizTitle>

      {quiz.problem && (
        <div className={cn(compact ? 'text-[15px]' : 'text-[16px]', 'leading-[30px] px-[40px] text-black')}>
          {formatText(quiz.problem, `${quizId}-problem`, quiz.additionalImages ?? undefined)}
        </div>
      )}

      {imageUrl && <PrintQuizImage src={imageUrl} printScale={printScale} />}

      <div className={cn('w-full px-[40px]', compact ? 'space-y-[10px]' : 'space-y-[14px]')}>
        {(quiz.children ?? []).map((child, childIdx) => (
          <InlineChildBlockPrint
            key={child.scriptId || childIdx}
            quizId={quizId}
            child={child}
            childIdx={childIdx}
            showCorrectAnswers={showCorrectAnswers}
            compact={compact}
            t={t}
          />
        ))}
      </div>
    </div>
  )
}

function InlineChildBlockPrint({
  quizId,
  child,
  childIdx,
  showCorrectAnswers,
  compact,
  t,
}: {
  quizId: string | number
  child: MultiChoiceQuizData
  childIdx: number
  showCorrectAnswers: boolean
  compact?: boolean

  t: any
}) {
  const choices =
    child.random && Array.isArray(child.random) && child.random.length > 0
      ? child.random.map(i => child.choices[i]).filter(Boolean)
      : child.choices

  const correctKeys = new Set(child.choices.filter(c => c.correct).map(c => c.key))
  const imageUrl = getImageUrl(child.image, child.imagePrint)
  const printScale = getPrintScale(child.image, child.imagePrint)

  return (
    <div className="space-y-[6px]">
      <div className={cn(compact ? 'text-[13px]' : 'text-[14px]', 'font-bold text-black')}>
        {t('types.inline.childLabel', { n: childIdx + 1 })}
      </div>
      {child.problem && (
        <div className={cn(compact ? 'text-[14px]' : 'text-[15px]', 'leading-[28px] text-black')}>
          {formatText(child.problem, `${quizId}-child-${childIdx}-problem`, child.additionalImages ?? undefined)}
        </div>
      )}
      {imageUrl && <PrintQuizImage src={imageUrl} printScale={printScale} />}
      <div className="flex flex-wrap gap-[12px] pointer-events-none">
        {choices.map((choice, i) => {
          const isCorrect = correctKeys.has(choice.key)
          const checked = showCorrectAnswers && isCorrect
          const choiceImageUrl = getImageUrl(choice.image)

          return (
            <div
              key={choice.key}
              className={cn(
                'inline-flex items-center gap-[6px] px-[10px] py-[4px] border border-black/40 rounded-md',
                checked && PRINT_CORRECT_ANSWER_CLASS,
              )}
              style={checked ? { borderColor: '#e50000' } : undefined}
            >
              <span
                className={cn(
                  compact ? 'text-[14px]' : 'text-[16px]',
                  'font-light',
                  checked ? PRINT_CORRECT_ANSWER_CLASS : 'text-black',
                )}
              >
                {CIRCLED_NUMBERS[i] || `${i + 1}`}
              </span>
              {choiceImageUrl && (
                <img
                  src={choiceImageUrl}
                  alt={`option-${i + 1}`}
                  className="max-h-[28px] w-auto object-contain shrink-0"
                />
              )}
              {choice.answer && (
                <span
                  className={cn(
                    compact ? 'text-[13px]' : 'text-[14px]',
                    'leading-[24px]',
                    checked ? PRINT_CORRECT_ANSWER_CLASS : 'text-black',
                  )}
                >
                  <KatexText text={choice.answer} />
                </span>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
